import type { QueryClient, QueryKey, Updater } from "@tanstack/vue-query";

export const pushUpdater: <T>(data: T) => Updater<T[] | undefined, T[]> =
  (data) => (old) => [...(old ?? []), data];

type Predicate<T> = (value: T) => boolean;
type UpdateFn<T> = (old: T[], index: number) => T[];

const genericUpdater: <T>(
  predicate: Predicate<T>,
  updateFn: UpdateFn<T>
) => Updater<T[] | undefined, T[] | undefined> =
  (predicate, updateFn) => (old) => {
    const oldIndex = old?.findIndex(predicate);

    if (oldIndex === undefined || oldIndex === -1) {
      return old;
    }

    const oldCpy = [...(old ?? [])];
    return updateFn(oldCpy, oldIndex);
  };

export const replaceUpdater: <T>(
  data: T,
  predicate: Predicate<T>
) => Updater<T[] | undefined, T[] | undefined> = (data, predicate) =>
  genericUpdater(predicate, (old, oldIndex) => {
    old[oldIndex] = data;

    return old;
  });

export const deleteUpdater: <T>(
  predicate: (value: T) => boolean
) => Updater<T[] | undefined, T[] | undefined> = (predicate) =>
  genericUpdater(predicate, (old, oldIndex) => {
    old.splice(oldIndex, 1);

    return old;
  });

export const invalidateKeys = (client: QueryClient, keys: QueryKey[]) =>
  Promise.all([
    ...keys.map((queryKey) => client.invalidateQueries({ queryKey })),
  ]);
